import { parseChordName } from './chordName';
import { QUALITIES, type ChordType, type QualitySlug } from './chordQualities';
import { ROOTS, getRoot, parseRoot, spell, type IntervalName, type RootSlug } from './notes';
import { fold } from '../utils/textNormalize';

export interface Chord {
  /** `<root>-<quality>`, for example `a-m`, `cs-maj7`. */
  id: string;
  root: RootSlug;
  quality: QualitySlug;
  rootDisplay: string;
  /** Root display plus suffix, for example `Am`, `C#maj7`; major has an empty suffix. */
  symbol: string;
  nameFa: string;
  nameEn: string;
  type: ChordType;
  intervals: readonly IntervalName[];
  /** Spelled chord tones, aligned with `intervals`. */
  notes: readonly string[];
}

export const chordIdOf = (root: RootSlug, quality: QualitySlug): string => `${root}-${quality}`;

/** 192 chords, roots C to B, then qualities in table order. */
export const CHORDS: readonly Chord[] = ROOTS.flatMap((r) =>
  QUALITIES.map((q): Chord => ({
    id: chordIdOf(r.slug, q.slug),
    root: r.slug,
    quality: q.slug,
    rootDisplay: r.display,
    symbol: r.display + q.suffix,
    nameFa: `${r.fa} ${q.nameFa}`,
    nameEn: q.nameEn.replace('{r}', r.display),
    type: q.type,
    intervals: q.intervals,
    notes: q.intervals.map((i) => spell(r.slug, i))
  }))
);

const BY_ID = new Map<string, Chord>(CHORDS.map((c) => [c.id, c]));
const ORDER = new Map<string, number>(CHORDS.map((c, i) => [c.id, i]));

export const getChord = (id: string): Chord | undefined => BY_ID.get(id);
export const chordSymbol = (id: string): string | undefined => BY_ID.get(id)?.symbol;
export const chordNotes = (id: string): readonly string[] => BY_ID.get(id)?.notes ?? [];

const norm = (s: string): string => fold(s).replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06f0));

interface SearchKeys {
  /** Exact-case forms that rank first: the symbol and root + alias. */
  exact: string[];
  /** Folded forms for substring matching. */
  haystack: string[];
}

const KEYS = new Map<string, SearchKeys>(
  CHORDS.map((c) => {
    const q = QUALITIES.find((x) => x.slug === c.quality)!;
    const aliasForms = q.aliases.map((a) => c.rootDisplay + a);
    return [
      c.id,
      {
        exact: [c.symbol, ...aliasForms],
        haystack: [c.symbol, ...aliasForms, c.nameEn, c.nameFa].map(norm)
      }
    ];
  })
);

export interface ChordSearchFilters {
  root?: RootSlug | null;
  type?: ChordType | null;
}

/**
 * Local chord search (AC-03, AC-04, TL-17). Filters are ANDed. Ranks: 0 exact symbol or alias with the
 * same case, 1 resolved by the name parser, 2 symbol prefix, 3 substring of names and aliases (only when the query is not a full chord symbol).
 */
export function searchChords(query: string, filters: ChordSearchFilters = {}): Chord[] {
  const pool = CHORDS.filter(
    (c) => (!filters.root || c.root === filters.root) && (!filters.type || c.type === filters.type)
  );
  const q = query.trim();
  if (!q) return pool.slice();

  const parsed = parseChordName(q);
  const resolvedId = parsed.root && parsed.quality ? chordIdOf(parsed.root, parsed.quality) : null;

  // Prefix form: map an enharmonic root spelling to the canonical display (A#m -> Bbm).
  let prefix = q;
  const rm = /^([A-Ga-g])(#|b|♯|♭)?/.exec(q);
  if (rm) {
    const slug = parseRoot(rm[0]);
    if (slug) prefix = getRoot(slug).display + q.slice(rm[0].length);
  }
  prefix = prefix.toLowerCase();
  const needle = norm(q);

  const ranked: Array<{ chord: Chord; rank: number }> = [];
  for (const c of pool) {
    const keys = KEYS.get(c.id)!;
    let rank = 4;
    if (c.id === resolvedId) rank = keys.exact.includes(q) ? 0 : 1;
    if (rank > 2 && c.symbol.toLowerCase().startsWith(prefix)) rank = 2;
    // A query that names a chord (Am, C#m7) is a symbol, not loose text: substring hits such as Am in "Amaj" (A) are noise.
    if (rank > 3 && !resolvedId && keys.haystack.some((h) => h.includes(needle))) rank = 3;
    if (rank < 4) ranked.push({ chord: c, rank });
  }
  ranked.sort((a, b) => a.rank - b.rank || ORDER.get(a.chord.id)! - ORDER.get(b.chord.id)!);
  return ranked.map((r) => r.chord);
}

// F-018: Guest demo chords (C, G, Am, Em, D, F)
export const GUEST_DEMO_CHORD_IDS = new Set<string>([
  'c-maj',
  'g-maj',
  'a-m',
  'e-m',
  'd-maj',
  'f-maj'
]);

export const GUEST_DEMO_CHORD_SYMBOLS = new Set<string>([
  'C',
  'G',
  'Am',
  'Em',
  'D',
  'F'
]);

export function isGuestDemoChord(chordIdOrSymbol: string): boolean {
  return GUEST_DEMO_CHORD_IDS.has(chordIdOrSymbol) || GUEST_DEMO_CHORD_SYMBOLS.has(chordIdOrSymbol);
}
